import React from "react";
import "../css/EditCategory.css";
import ImageUploader from "@components/upload/ImageUploader";
import { useCategoriesQuery } from "@queries/catalog";

// Descriptions / html-details are stored as HTML (rendered as HTML on the app,
// like the old Summernote) but edited in a plain <textarea>. Decode the stored
// HTML into clean editable text on load — handling double-encoded entities such
// as &amp;nbsp; and &amp;lt;p&amp;gt; — and convert newlines back to <br> on save.
const htmlToText = (input) => {
  let s = String(input ?? "");
  if (!s) return "";
  const ta = document.createElement("textarea");
  const decode = (str) => { ta.innerHTML = str; return ta.value; };
  for (let i = 0; i < 3; i++) { const d = decode(s); if (d === s) break; s = d; }
  s = s
    .replace(/<\/(p|div|li|h[1-6])\s*>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "");
  return s.replace(/ /g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
};
const textToHtml = (s) => String(s ?? "").replace(/\r\n?|\n/g, "<br>");

function Tabs({ value, onChange, items, size = "md" }) {
  return (
    <div className={`oc-tabs ${size === "sm" ? "small" : ""}`}>
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          className={`oc-tab ${value === it.value ? "active" : ""}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export default function CategoryForm({ initial = {}, onSubmit, onCancel }) {
  const [tab, setTab] = React.useState("general");
  const [lang, setLang] = React.useState("ar");

  const [form, setForm] = React.useState(() => {
    const ar = initial.descriptions?.ar || {};
    const en = initial.descriptions?.en || {};

    return {
      id: initial.id ?? null,
      parent_id: initial.parent_id ?? 0,
      image: initial.image ?? "",
      top: Number(initial.top ?? 0),
      column: Number(initial.column ?? 1),
      sort_order: Number(initial.sort_order ?? 0),
      status: Number(initial.status ?? 1),
      descriptions: {
        ar: {
          language_id: ar.language_id ?? 2,
          name: ar.name ?? "",
          description: htmlToText(ar.description ?? ""),
          html_details: htmlToText(ar.html_details ?? ""),
          meta_title: ar.meta_title ?? "",
          meta_description: ar.meta_description ?? "",
          meta_keyword: ar.meta_keyword ?? ""
        },
        en: {
          language_id: en.language_id ?? 1,
          name: en.name ?? "",
          description: htmlToText(en.description ?? ""),
          html_details: htmlToText(en.html_details ?? ""),
          meta_title: en.meta_title ?? "",
          meta_description: en.meta_description ?? "",
          meta_keyword: en.meta_keyword ?? ""
        }
      }
    };
  });

  const [parentSearch, setParentSearch] = React.useState("");
  const [parentName, setParentName] = React.useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDesc = (loc, k, v) =>
    setForm((f) => ({
      ...f,
      descriptions: { ...f.descriptions, [loc]: { ...f.descriptions[loc], [k]: v } }
    }));

  const submit = (e) => {
    e?.preventDefault?.();
    // Persist description + html details as HTML so they stay organized on the app.
    const payload = {
      ...form,
      descriptions: {
        ar: { ...form.descriptions.ar, description: textToHtml(form.descriptions.ar.description), html_details: textToHtml(form.descriptions.ar.html_details) },
        en: { ...form.descriptions.en, description: textToHtml(form.descriptions.en.description), html_details: textToHtml(form.descriptions.en.html_details) },
      },
    };
    onSubmit?.(payload);
  };

  const D = form.descriptions[lang];

  const {
    data: categoriesData,
    isLoading: catsLoading,
    isError: catsError
  } = useCategoriesQuery(
    parentSearch ? { name: parentSearch } : {}, // adjust filter key if needed
    1,
    50
  );

  const parentOptions = categoriesData?.data || [];

  return (
    <form onSubmit={submit}>
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "general", label: "General" },
          { value: "data", label: "Data" },
          { value: "seo", label: "SEO" },
          { value: "design", label: "Design" }
        ]}
      />

      {tab === "general" && (
        <div className="oc-tab-pane">
          <Tabs
            value={lang}
            onChange={setLang}
            items={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" }
            ]}
            size="sm"
          />

          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Category Name (Arabic)"
                  : "Category Name (English)"}
              </label>
              <input
                className="oc-input"
                value={D.name}
                onChange={(e) => setDesc(lang, "name", e.target.value)}
              />
            </div>

            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Html details (Arabic)"
                  : "Html details (English)"}
              </label>
              <textarea
                className="oc-input"
                rows={4}
                value={D.html_details}
                onChange={(e) => setDesc(lang, "html_details", e.target.value)}
              />
            </div>

            <div className="oc-field" style={{ gridColumn: "1 / span 2" }}>
              <label>
                {lang === "ar"
                  ? "Description (Arabic)"
                  : "Description (English)"}
              </label>
              <textarea
                className="oc-input"
                rows={8}
                value={D.description}
                onChange={(e) => setDesc(lang, "description", e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {tab === "data" && (
        <div className="oc-tab-pane">
          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            
          {/* Parent category selector */}
        <div className="oc-field">
          <label>Parent Category</label>
          <div className="oc-parent-select">
            <div className="oc-parent-row">
              <input
                className="oc-input"
                placeholder="Search categories..."
                value={parentSearch}
                onChange={(e) => setParentSearch(e.target.value)}
              />
              <button
                type="button"
                className="oc-btn ghost oc-parent-clear"
                onClick={() => {
                  setField("parent_id", 0);
                  setParentName("");
                }}
              >
                No parent
              </button>
            </div>

            <div className="oc-parent-current">
              {form.parent_id
                ? (
                  <>
                    <span className="oc-parent-current-label">Selected:</span>
                    <span className="oc-parent-current-value">
                      {parentName || "ID"}#{form.parent_id}
                    </span>
                  </>
                )
                : (
                  <>
                    <span className="oc-parent-current-label">Currently:</span>
                    <span className="oc-parent-current-value">root category</span>
                  </>
                )}
            </div>

            <div className="oc-parent-list">
              {catsLoading && (
                <div className="oc-parent-empty">Loading...</div>
              )}
              {catsError && (
                <div className="oc-parent-empty oc-field-error">
                  Error loading categories
                </div>
              )}
              {!catsLoading &&
                !catsError &&
                parentOptions.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={
                      "oc-parent-item" +
                      (form.parent_id === cat.id ? " active" : "")
                    }
                    onClick={() => {
                      setField("parent_id", Number(cat.id));
                      setParentName(cat.name || "");
                    }}
                  >
                    <span className="oc-parent-name">{cat.name}</span>
                    <span className="oc-parent-id">#{cat.id}</span>
                  </button>
                ))}
              {!catsLoading &&
                !catsError &&
                parentOptions.length === 0 &&
                parentSearch && (
                  <div className="oc-parent-empty">No categories found</div>
                )}
            </div>
          </div>
        </div>


            <div className="oc-field">
              <label>Image</label>
              <div className="oc-image-input-row">
                <input
                  className="oc-input"
                  value={form.image}
                  placeholder="img_xxx.png (path)"
                  onChange={(e) => setField("image", e.target.value)}
                />
                <ImageUploader
                  value={form.image}
                  onChange={(newPath) => {
                    if (newPath) setField("image", newPath);
                  }}
                />
              </div>
            </div>

            <div className="oc-field">
              <label>Top</label>
              <div style={{ display: "flex", alignItems: "center", height: 38 }}>
                <input
                  type="checkbox"
                  checked={!!form.top}
                  onChange={(e) => setField("top", e.target.checked ? 1 : 0)}
                />
              </div>
            </div>

            <div className="oc-field">
              <label>Columns</label>
              <input
                className="oc-input"
                type="number"
                value={form.column}
                onChange={(e) =>
                  setField("column", Number(e.target.value || 1))
                }
              />
            </div>

            <div className="oc-field">
              <label>Sort Order</label>
              <input
                className="oc-input"
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setField("sort_order", Number(e.target.value || 0))
                }
              />
            </div>

            <div className="oc-field">
              <label>Status</label>
              <select
                className="oc-select"
                value={form.status}
                onChange={(e) =>
                  setField("status", Number(e.target.value))
                }
              >
                <option value={1}>Enabled</option>
                <option value={0}>Disabled</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {tab === "seo" && (
        <div className="oc-tab-pane">
          <Tabs
            value={lang}
            onChange={setLang}
            items={[
              { value: "ar", label: "Arabic" },
              { value: "en", label: "English" }
            ]}
            size="sm"
          />
          <div className="oc-f-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Meta Title (Arabic)"
                  : "Meta Title (English)"}
              </label>
              <input
                className="oc-input"
                value={D.meta_title}
                onChange={(e) => setDesc(lang, "meta_title", e.target.value)}
              />
            </div>
            <div className="oc-field">
              <label>
                {lang === "ar"
                  ? "Meta Keywords (Arabic)"
                  : "Meta Keywords (English)"}
              </label>
              <input
                className="oc-input"
                value={D.meta_keyword}
                onChange={(e) => setDesc(lang, "meta_keyword", e.target.value)}
              />
            </div>
            <div className="oc-field" style={{ gridColumn: "1 / span 2" }}>
              <label>
                {lang === "ar"
                  ? "Meta Description (Arabic)"
                  : "Meta Description (English)"}
              </label>
              <textarea
                className="oc-input"
                rows={4}
                value={D.meta_description}
                onChange={(e) =>
                  setDesc(lang, "meta_description", e.target.value)
                }
              />
            </div>
          </div>
        </div>
      )}

      {tab === "design" && (
        <div className="oc-tab-pane">
          <div className="oc-muted">No fields</div>
        </div>
      )}

      <div
        className="oc-actions"
        style={{ marginTop: 16, display: "flex", gap: 8 }}
      >
        <button className="oc-btn" type="submit">
          Save
        </button>
        <button
          className="oc-btn ghost"
          type="button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

